import { DeleteOutlined, EditFilled, SendOutlined } from '@ant-design/icons'
import { Button, TableActions } from '@/components/ui'
import type { TableAction } from '@/components/ui'
import {
  AdminTable,
  type AdminTableColumn,
  type TableDataItem,
} from '@/components/layouts/events/AdminTable'
import dayjs from 'dayjs'

import { StatusBadge } from './StatusBadge'
import type { PushNotification } from './types'

interface PushNotificationsTableProps {
  data: PushNotification[]
  onSend: (notification: PushNotification) => void
  onEdit: (notification: PushNotification) => void
  onDelete: (notification: PushNotification) => void
  onViewRecipients: (notification: PushNotification) => void
}

const formatTimestamp = (isoString: string | null) => {
  if (!isoString) return '-'

  return dayjs(isoString).format('YYYY-MM-DD, hh:mmA')
}

type PushNotificationRow = PushNotification & TableDataItem

export const PushNotificationsTable: React.FC<PushNotificationsTableProps> = ({
  data,
  onSend,
  onEdit,
  onDelete,
  onViewRecipients,
}) => {
  const columns: AdminTableColumn<PushNotificationRow>[] = [
    {
      title: 'Push Notification',
      dataIndex: 'title',
      sortable: true,
      render: (text: string) => (
        <span className="text-sm text-black/85">{text}</span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-sm text-black/85">{formatTimestamp(value)}</span>
      ),
      width: 190,
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-sm text-black/85">{formatTimestamp(value)}</span>
      ),
      width: 190,
    },
    {
      title: 'Recipients',
      dataIndex: 'recipientCount',
      sortable: true,
      align: 'center',
      width: 120,
      render: (_: number, record: PushNotificationRow) => (
        <button
          type="button"
          className="text-sm text-ava-blue-07 underline cursor-pointer hover:underline"
          onClick={() => onViewRecipients(record)}
        >
          {record.recipientCount}
        </button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (_: string, record: PushNotificationRow) => (
        <StatusBadge status={record.status} />
      ),
    },
    {
      title: 'Actions',
      width: 240,
      align: 'center',
      render: (_: unknown, record: PushNotificationRow) => {
        const isSent = record.status === 'sent'

        const iconActions: TableAction[] = [
          {
            key: 'edit',
            icon: <EditFilled />,
            onClick: () => onEdit(record),
            ariaLabel: 'Edit notification',
            disabled: isSent,
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: () => onDelete(record),
            ariaLabel: 'Delete notification',
            danger: true,
            disabled: isSent,
          },
        ]

        return (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="tertiary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => onSend(record)}
              disabled={isSent}
            >
              Send
            </Button>
            <TableActions actions={iconActions} />
          </div>
        )
      },
    },
  ]
  const tableData: PushNotificationRow[] = data.map((notification) => ({
    ...notification,
    key: notification.id,
  }))

  return (
    <AdminTable<PushNotificationRow>
      className="ava-data-table"
      columns={columns}
      dataSource={tableData}
      pagination={false}
      bordered={false}
    />
  )
}
